'use client'

import { ProductAvailabilities } from '@/feature/product/components/product-availabilities/ProductAvailabilities.client'
import { ProductAvailabilitiesSkeleton } from '@/feature/product/components/product-availabilities/ProductAvailabilitiesSkeleton'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import type {
  ProductAvailabilities_PickupPointOfSaleFragment,
  ProductBuyBox_CartFragment,
  ProductBuyBox_FavoritePointOfSaleFragment,
} from '@gql-generated/graphql'
import {
  type ProductBuyBox_ProductFragment,
  ProductBuyBoxPriceAndStockDocument,
} from '@gql-generated/graphql'
import { useAtomValue, useSetAtom } from 'jotai'
import { useSearchParams } from 'next/navigation'
import { Suspense, useEffect } from 'react'
import { productBuyBoxAddToCartAtom } from '../../services/product.store'
import type { MergedProduct } from '../../types/mergedProduct'
import { ProductBuyBox } from './ProductBuyBox.client'
import { ProductBuyBoxSkeleton } from './ProductBuyBoxSkeleton.client'

gql(/* GraphQL */ `
  fragment ProductBuyBox_ProductPriceAndStock on Product {
    ...ProductAvailabilities_Product
    ...ProductCalculatedPrice
    reference
    bestArticlePrice
    ecoPart {
      deee
      pmcb
      furnishing
    }
    charges {
      name
      amount
    }
    isEquivalentProductsLinkVisible
    stock {
      packaging {
        ...FullPackaging
      }
    }
    price {
      referenceExclTax
      ...FullPrice
      ...TiersPrice
      discountExclTax
      discountInclTax
      discountPercentage
    }
  }

  query ProductBuyBoxPriceAndStock($reference: String!) {
    productByReference(reference: $reference) {
      ...ProductBuyBox_ProductPriceAndStock
      reference
      parentReference
      analytics {
        promotionStatus
        profileCase
      }
    }
  }
`)

export type ProductBuyBoxAndAvailabilitiesProps = {
  product: ProductBuyBox_ProductFragment
  /** Current User */
  favoritePointOfSale?: ProductBuyBox_FavoritePointOfSaleFragment | null
  cart: ProductBuyBox_CartFragment | null | undefined
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
  isClickAndCollectEnabled?: boolean
}

export function ProductBuyBoxAndAvailabilities({
  product,
  cart,
  favoritePointOfSale,
  pickupPointOfSale,
  isClickAndCollectEnabled = false,
}: ProductBuyBoxAndAvailabilitiesProps): React.ReactElement {
  const productBuyBoxAddToCart = useAtomValue(productBuyBoxAddToCartAtom)
  const setProductBuyBoxAddToCart = useSetAtom(productBuyBoxAddToCartAtom)
  const searchParams = useSearchParams()

  // Derive effective view type from query param first, else fallback to page product.type
  const effectiveType = searchParams?.get('type')?.toUpperCase() || product.type
  const { data: priceAndStockData, loading: isArticleQueryLoading } = useQuery(
    ProductBuyBoxPriceAndStockDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: { reference: product.reference },
    },
  )
  // BUT we don't treat subsequent background loads as loading if we already have data
  const isArticleLoading = isArticleQueryLoading && !priceAndStockData

  const minimalSalesQuantity =
    priceAndStockData?.productByReference?.stock?.packaging?.minimalSalesQuantity ?? 1
  useEffect(() => {
    setProductBuyBoxAddToCart({
      quantity: minimalSalesQuantity,
    })
  }, [product.reference, minimalSalesQuantity, setProductBuyBoxAddToCart])

  // Merge product data with price and stock data for ProductAvailabilities
  const mergedProduct: MergedProduct = {
    ...product,
    ...priceAndStockData?.productByReference,
    type: effectiveType,
  } as MergedProduct

  return (
    <>
      <Suspense fallback={<ProductBuyBoxSkeleton />}>
        <ProductBuyBox
          product={mergedProduct}
          priceAndStockIsLoading={isArticleLoading}
          cartId={cart?.id}
          favoritePOSPhoneNumber={favoritePointOfSale?.phoneNumber ?? undefined}
        />
      </Suspense>

      <Suspense
        fallback={
          <ProductAvailabilitiesSkeleton
            offset={[
              { nbOfLine: 1, title: true },
              { nbOfLine: 1, title: true },
            ]}
          />
        }
      >
        <ProductAvailabilities
          pickupPointOfSale={pickupPointOfSale}
          product={mergedProduct}
          className="mt-3 empty:hidden"
          isClickAndCollectEnabled={isClickAndCollectEnabled}
          selectedQuantity={productBuyBoxAddToCart.quantity}
          isClarityDelayEnabled={Boolean(productBuyBoxAddToCart.quantity)}
        />
      </Suspense>
    </>
  )
}
